import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, Search, Plus, Check, X, Edit, Trash2, Eye, Loader2,
  MapPin, Phone, Mail, Calendar, IndianRupee, Filter, Download,
  RefreshCw, ChevronDown, FileSpreadsheet
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PARTNER_FEES = {
  SP: 500000,
  DLC: 150000,
  TC: 50000
};

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh"
];

const AdminFranchisePage = () => {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [centers, setCenters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('applications');
  
  // New filter states
  const [stateFilter, setStateFilter] = useState('all');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog states
  const [addPartnerOpen, setAddPartnerOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingApplication, setEditingApplication] = useState(false);
  const [deletingApplication, setDeletingApplication] = useState(false);
  
  // Form states
  const [partnerDistricts, setPartnerDistricts] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [creatingPartner, setCreatingPartner] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [partnerForm, setPartnerForm] = useState({
    partner_type: 'TC',
    center_name: '',
    owner_name: '',
    email: '',
    mobile: '',
    state: '',
    district: '',
    city: '',
    pin_code: '',
    address: '',
    password: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, centersRes] = await Promise.all([
        axios.get(`${API}/admin/franchise-applications`),
        axios.get(`${API}/admin/centers`)
      ]);
      setApplications(appsRes.data || []);
      setCenters(centersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load franchise data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistricts = async (state) => {
    if (!state) return;
    setLoadingDistricts(true);
    try {
      const response = await axios.get(`${API}/districts/${encodeURIComponent(state)}`);
      setPartnerDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Failed to fetch districts:', error);
      setPartnerDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  const handleApproveStage1 = async (appId) => {
    try {
      await axios.post(`${API}/partner/approve/${appId}`);
      toast.success('Application approved for Stage 1');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (appId) => {
    try {
      await axios.post(`${API}/admin/franchise/reject/${appId}`);
      toast.success('Application rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleCreatePartner = async (e) => {
    e.preventDefault();
    setCreatingPartner(true);
    try {
      await axios.post(`${API}/admin/create-partner`, partnerForm);
      toast.success('Partner created successfully');
      setAddPartnerOpen(false);
      setPartnerForm({
        partner_type: 'TC',
        center_name: '',
        owner_name: '',
        email: '',
        mobile: '',
        state: '',
        district: '',
        city: '',
        pin_code: '',
        address: '',
        password: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create partner');
    } finally {
      setCreatingPartner(false);
    }
  };

  const handleDeleteCenter = async () => {
    if (!selectedItem) return;
    try {
      await axios.delete(`${API}/admin/centers/${selectedItem.id}`);
      toast.success('Center deleted successfully');
      setDeleteConfirmOpen(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Edit Center Handler
  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    try {
      await axios.put(`${API}/admin/centers/${selectedItem.id}`, {
        center_name: selectedItem.center_name,
        owner_name: selectedItem.owner_name,
        email: selectedItem.email,
        mobile: selectedItem.mobile,
        state: selectedItem.state,
        district: selectedItem.district,
        city: selectedItem.city,
        address: selectedItem.address,
        status: selectedItem.status
      });
      toast.success('Center updated successfully');
      setEditDialogOpen(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update center');
    }
  };

  // Edit Application Handler
  const handleEditApplication = async () => {
    if (!selectedItem) return;
    setEditingApplication(true);
    try {
      await axios.put(`${API}/admin/franchise-applications/${selectedItem.id}`, editForm);
      toast.success('Application updated successfully');
      setEditDialogOpen(false);
      setSelectedItem(null);
      setEditForm({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    } finally {
      setEditingApplication(false);
    }
  };

  // Delete Application Handler
  const handleDeleteApplication = async () => {
    if (!selectedItem) return;
    setDeletingApplication(true);
    try {
      await axios.delete(`${API}/admin/franchise-applications/${selectedItem.id}`);
      toast.success('Application deleted successfully');
      setDeleteConfirmOpen(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    } finally {
      setDeletingApplication(false);
    }
  };

  // Export to Excel function
  const exportToExcel = (data, filename) => {
    // Create CSV content
    const headers = activeTab === 'applications' 
      ? ['Application ID', 'Center Name', 'Owner Name', 'Email', 'Mobile', 'Partner Type', 'State', 'District', 'City', 'Status', 'Submitted At']
      : ['Center Code', 'Center Name', 'Owner Name', 'Email', 'Mobile', 'Partner Type', 'State', 'District', 'City', 'Status', 'Total Students', 'Total Revenue'];
    
    const rows = data.map(item => {
      if (activeTab === 'applications') {
        return [
          item.id || '',
          item.center_name || '',
          item.owner_name || item.spoc_name || '',
          item.email || '',
          item.mobile || '',
          item.partner_type || 'TC',
          item.state || '',
          item.district || '',
          item.city || '',
          item.status || '',
          item.submitted_at || ''
        ];
      } else {
        return [
          item.center_code || '',
          item.center_name || '',
          item.owner_name || '',
          item.email || '',
          item.mobile || '',
          item.partner_type || 'TC',
          item.state || '',
          item.district || '',
          item.city || '',
          item.status || '',
          item.total_students || 0,
          item.total_revenue || 0
        ];
      }
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Excel file downloaded successfully');
  };

  // Get unique states and districts for filters
  const uniqueStates = [...new Set([...applications, ...centers].map(item => item.state).filter(Boolean))];
  const uniqueDistricts = [...new Set([...applications, ...centers].map(item => item.district).filter(Boolean))];

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.center_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.id?.toLowerCase().includes(searchTerm.toLowerCase());
    // Handle both old and new status names
    let matchesStatus = statusFilter === 'all';
    if (statusFilter === 'pending') {
      matchesStatus = app.status === 'pending' || app.status === 'UNDER_REVIEW';
    } else if (statusFilter === 'stage1_approved') {
      matchesStatus = app.status === 'stage1_approved' || app.status === 'APPROVED_PENDING_PAYMENT';
    } else if (statusFilter === 'active') {
      matchesStatus = app.status === 'active' || app.status === 'ACTIVE';
    } else if (statusFilter === 'rejected') {
      matchesStatus = app.status === 'rejected' || app.status === 'REJECTED';
    } else if (statusFilter !== 'all') {
      matchesStatus = app.status === statusFilter;
    }
    // State and District filters
    const matchesState = stateFilter === 'all' || app.state === stateFilter;
    const matchesDistrict = districtFilter === 'all' || app.district === districtFilter;
    return matchesSearch && matchesStatus && matchesState && matchesDistrict;
  });

  const filteredCenters = centers.filter(center => {
    const matchesSearch = 
      center.center_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      center.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      center.center_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      center.email?.toLowerCase().includes(searchTerm.toLowerCase());
    // State and District filters
    const matchesState = stateFilter === 'all' || center.state === stateFilter;
    const matchesDistrict = districtFilter === 'all' || center.district === districtFilter;
    return matchesSearch && matchesState && matchesDistrict;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-franchise-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Franchise Management</h1>
          <p className="text-slate-500 mt-1">
            {applications.filter(a => a.status === 'pending' || a.status === 'UNDER_REVIEW').length} pending applications
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={() => setAddPartnerOpen(true)} className="bg-purple-600 hover:bg-purple-700 gap-2">
            <Plus className="w-4 h-4" />
            Add Partner
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('applications')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'applications' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Applications ({applications.length})
        </button>
        <button
          onClick={() => setActiveTab('centers')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'centers' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Active Centers ({centers.length})
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        {activeTab === 'applications' && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending / Under Review</option>
            <option value="stage1_approved">Stage 1 Approved</option>
            <option value="payment_pending">Payment Pending</option>
            <option value="active">Active</option>
            <option value="rejected">Rejected</option>
          </select>
        )}
        
        {/* State Filter */}
        <select
          value={stateFilter}
          onChange={(e) => { setStateFilter(e.target.value); setDistrictFilter('all'); }}
          className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
        >
          <option value="all">All States</option>
          {uniqueStates.sort().map(state => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
        
        {/* District Filter */}
        <select
          value={districtFilter}
          onChange={(e) => setDistrictFilter(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
        >
          <option value="all">All Districts</option>
          {uniqueDistricts.sort().map(district => (
            <option key={district} value={district}>{district}</option>
          ))}
        </select>
        
        {/* Clear Filters */}
        {(stateFilter !== 'all' || districtFilter !== 'all' || statusFilter !== 'all') && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => { setStateFilter('all'); setDistrictFilter('all'); setStatusFilter('all'); }}
            className="text-slate-500"
          >
            Clear Filters
          </Button>
        )}
        
        {/* Export Button */}
        <Button 
          variant="outline" 
          className="gap-2 ml-auto"
          onClick={() => exportToExcel(
            activeTab === 'applications' ? filteredApplications : filteredCenters,
            activeTab === 'applications' ? 'franchise_applications' : 'active_centers'
          )}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Export Excel
        </Button>
      </div>

      {/* Applications Table */}
      {activeTab === 'applications' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Application ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Center Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Partner Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Location</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    No applications found
                  </td>
                </tr>
              ) : (
                filteredApplications.map((app) => (
                  <tr key={app.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-slate-600">{app.application_id || app.id?.substring(0,8)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{app.center_name || `${app.owner_name}'s Center`}</p>
                        <p className="text-sm text-slate-500">{app.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        app.partner_type === 'SP' ? 'bg-purple-100 text-purple-700' :
                        app.partner_type === 'DLC' ? 'bg-blue-100 text-blue-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {app.partner_type === 'SP' ? 'State Partner' :
                         app.partner_type === 'DLC' ? 'District Center' :
                         'Training Center'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <MapPin className="w-3 h-3" />
                        {app.city}, {app.state}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        (app.status === 'pending' || app.status === 'UNDER_REVIEW') ? 'bg-amber-100 text-amber-700' :
                        (app.status === 'stage1_approved' || app.status === 'APPROVED_PENDING_PAYMENT') ? 'bg-blue-100 text-blue-700' :
                        app.status === 'payment_pending' ? 'bg-purple-100 text-purple-700' :
                        (app.status === 'active' || app.status === 'ACTIVE') ? 'bg-emerald-100 text-emerald-700' :
                        (app.status === 'rejected' || app.status === 'REJECTED') ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {(app.status === 'pending' || app.status === 'UNDER_REVIEW') ? 'Pending Review' :
                         (app.status === 'stage1_approved' || app.status === 'APPROVED_PENDING_PAYMENT') ? 'Stage 1 Approved' :
                         app.status === 'payment_pending' ? 'Payment Pending' :
                         (app.status === 'active' || app.status === 'ACTIVE') ? 'Active' :
                         (app.status === 'rejected' || app.status === 'REJECTED') ? 'Rejected' : app.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedItem(app); setViewDialogOpen(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {(app.status === 'pending' || app.status === 'UNDER_REVIEW') && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-emerald-600 hover:bg-emerald-50"
                              onClick={() => handleApproveStage1(app.id)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleReject(app.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {/* Edit Button - Always visible */}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={() => { 
                            setSelectedItem(app); 
                            setEditForm({
                              center_name: app.center_name || '',
                              owner_name: app.owner_name || app.spoc_name || '',
                              email: app.email || '',
                              mobile: app.mobile || '',
                              state: app.state || '',
                              district: app.district || '',
                              city: app.city || '',
                              address: app.address || '',
                              partner_type: app.partner_type || 'TC'
                            });
                            setEditDialogOpen(true); 
                          }}
                          title="Edit Application"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {/* Delete Button */}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => { setSelectedItem(app); setDeleteConfirmOpen(true); }}
                          title="Delete Application"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Centers Table */}
      {activeTab === 'centers' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Center Code</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Center Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Owner</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Location</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredCenters.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    No centers found
                  </td>
                </tr>
              ) : (
                filteredCenters.map((center) => (
                  <tr key={center.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-blue-600">{center.center_code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{center.center_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-slate-900">{center.owner_name}</p>
                        <p className="text-sm text-slate-500">{center.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <MapPin className="w-3 h-3" />
                        {center.city}, {center.state}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        center.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {center.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedItem(center); setEditDialogOpen(true); }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => { setSelectedItem(center); setDeleteConfirmOpen(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Partner Dialog */}
      <Dialog open={addPartnerOpen} onOpenChange={setAddPartnerOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Add New Partner
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePartner} className="space-y-4">
            <div>
              <Label>Partner Type</Label>
              <select
                value={partnerForm.partner_type}
                onChange={(e) => setPartnerForm({...partnerForm, partner_type: e.target.value})}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"
                required
              >
                <option value="TC">Training Center (₹50,000)</option>
                <option value="DLC">District Level Center (₹1,50,000)</option>
                <option value="SP">State Partner (₹5,00,000)</option>
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Center Name</Label>
                <Input
                  value={partnerForm.center_name}
                  onChange={(e) => setPartnerForm({...partnerForm, center_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Owner Name</Label>
                <Input
                  value={partnerForm.owner_name}
                  onChange={(e) => setPartnerForm({...partnerForm, owner_name: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={partnerForm.email}
                  onChange={(e) => setPartnerForm({...partnerForm, email: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input
                  value={partnerForm.mobile}
                  onChange={(e) => setPartnerForm({...partnerForm, mobile: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>State</Label>
                <select
                  value={partnerForm.state}
                  onChange={(e) => {
                    setPartnerForm({...partnerForm, state: e.target.value, district: ''});
                    fetchDistricts(e.target.value);
                  }}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"
                  required
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>District</Label>
                <select
                  value={partnerForm.district}
                  onChange={(e) => setPartnerForm({...partnerForm, district: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"
                  required
                  disabled={loadingDistricts || !partnerForm.state}
                >
                  <option value="">Select District</option>
                  {partnerDistricts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={partnerForm.city}
                  onChange={(e) => setPartnerForm({...partnerForm, city: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>PIN Code</Label>
                <Input
                  value={partnerForm.pin_code}
                  onChange={(e) => setPartnerForm({...partnerForm, pin_code: e.target.value})}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Address</Label>
              <Input
                value={partnerForm.address}
                onChange={(e) => setPartnerForm({...partnerForm, address: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={partnerForm.password}
                onChange={(e) => setPartnerForm({...partnerForm, password: e.target.value})}
                required
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setAddPartnerOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creatingPartner} className="bg-purple-600 hover:bg-purple-700">
                {creatingPartner ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Partner
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Center Name</p>
                  <p className="font-medium">{selectedItem.center_name || `${selectedItem.owner_name}'s Center`}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Partner Type</p>
                  <p className="font-medium">{selectedItem.partner_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Owner Name</p>
                  <p className="font-medium">{selectedItem.owner_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedItem.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Mobile</p>
                  <p className="font-medium">{selectedItem.mobile}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="font-medium">{selectedItem.city}, {selectedItem.district}, {selectedItem.state}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <p className="font-medium capitalize">{selectedItem.status}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Fee</p>
                  <p className="font-medium">₹{(PARTNER_FEES[selectedItem.partner_type] || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete this center?
            </p>
            <p className="text-red-500 text-sm mt-2">
              Center: {selectedItem?.center_name}
            </p>
            <p className="text-slate-400 text-xs mt-2">This action cannot be undone.</p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDeleteCenter} className="bg-red-600 hover:bg-red-700">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog - For Both Applications and Centers */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Edit className="w-5 h-5" />
              {activeTab === 'applications' ? 'Edit Application' : 'Edit Training Center'}
            </DialogTitle>
          </DialogHeader>
          {selectedItem && activeTab === 'applications' ? (
            // Edit Application Form
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Center Name</Label>
                  <Input
                    value={editForm.center_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, center_name: e.target.value })}
                    placeholder="Center Name"
                  />
                </div>
                <div>
                  <Label>Owner/SPOC Name</Label>
                  <Input
                    value={editForm.owner_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, owner_name: e.target.value })}
                    placeholder="Owner/SPOC Name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.email || ''}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="Email"
                  />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input
                    value={editForm.mobile || ''}
                    onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                    placeholder="Mobile Number"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Partner Type</Label>
                  <select
                    value={editForm.partner_type || 'TC'}
                    onChange={(e) => setEditForm({ ...editForm, partner_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md"
                  >
                    <option value="TC">Training Center (₹50,000)</option>
                    <option value="DLC">District Level Center (₹1,50,000)</option>
                    <option value="SP">State Partner (₹5,00,000)</option>
                  </select>
                </div>
                <div>
                  <Label>State</Label>
                  <select
                    value={editForm.state || ''}
                    onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md"
                  >
                    <option value="">Select State</option>
                    {INDIAN_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>District</Label>
                  <Input
                    value={editForm.district || ''}
                    onChange={(e) => setEditForm({ ...editForm, district: e.target.value })}
                    placeholder="District"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={editForm.city || ''}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    placeholder="City"
                  />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={editForm.address || ''}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  placeholder="Full Address"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditForm({}); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleEditApplication} 
                  disabled={editingApplication}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {editingApplication ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          ) : selectedItem && (
            // Edit Center Form (existing)
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Center Name</Label>
                  <Input
                    value={selectedItem.center_name || ''}
                    onChange={(e) => setSelectedItem({ ...selectedItem, center_name: e.target.value })}
                    placeholder="Center Name"
                  />
                </div>
                <div>
                  <Label>SPOC Name</Label>
                  <Input
                    value={selectedItem.owner_name || ''}
                    onChange={(e) => setSelectedItem({ ...selectedItem, owner_name: e.target.value })}
                    placeholder="Owner/SPOC Name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={selectedItem.email || ''}
                    onChange={(e) => setSelectedItem({ ...selectedItem, email: e.target.value })}
                    placeholder="Email"
                  />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input
                    value={selectedItem.mobile || ''}
                    onChange={(e) => setSelectedItem({ ...selectedItem, mobile: e.target.value })}
                    placeholder="Mobile Number"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>State</Label>
                  <select
                    value={selectedItem.state || ''}
                    onChange={(e) => setSelectedItem({ ...selectedItem, state: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md"
                  >
                    <option value="">Select State</option>
                    {INDIAN_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>District</Label>
                  <Input
                    value={selectedItem.district || ''}
                    onChange={(e) => setSelectedItem({ ...selectedItem, district: e.target.value })}
                    placeholder="District"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={selectedItem.city || ''}
                    onChange={(e) => setSelectedItem({ ...selectedItem, city: e.target.value })}
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <select
                    value={selectedItem.status || 'active'}
                    onChange={(e) => setSelectedItem({ ...selectedItem, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md"
                  >
                    <option value="active">Active</option>
                    <option value="blocked">Blocked</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={selectedItem.address || ''}
                  onChange={(e) => setSelectedItem({ ...selectedItem, address: e.target.value })}
                  placeholder="Full Address"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} className="bg-amber-600 hover:bg-amber-700">
                  <Check className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              Are you sure you want to delete this {activeTab === 'applications' ? 'application' : 'center'}?
            </p>
            {selectedItem && (
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <p className="font-medium text-slate-900">{selectedItem.center_name || `${selectedItem.owner_name}'s Center`}</p>
                <p className="text-sm text-slate-500">{selectedItem.email}</p>
                {selectedItem.center_code && (
                  <p className="text-sm text-slate-500 font-mono">{selectedItem.center_code}</p>
                )}
              </div>
            )}
            <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
              ⚠️ This action cannot be undone. All associated data will be permanently deleted.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setDeleteConfirmOpen(false); setSelectedItem(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={activeTab === 'applications' ? handleDeleteApplication : handleDeleteCenter}
              disabled={deletingApplication}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingApplication ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFranchisePage;
